import { useLoginStore } from '@/login/stores/useLoginStore';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import { queryOptions } from '@tanstack/vue-query';
import type { UseMutationOptions } from '@tanstack/vue-query';
import { useQueryClient } from '@tanstack/vue-query';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import type { EnabledRoles } from '@sysarb/sysarb-types/services/v1/access/EnabledRoles';

export type RoleAccessState = EnabledRoles;

export type ToggleRolePayload = {
    role: Role;
    enabled: boolean;
};

// Toggle these to test error states
const simulateQueryError: boolean = false;
const simulateMutationError: boolean = false;

type RoleAccessOptions = {
    queries: {
        getRoleAccess: () => ReturnType<typeof queryOptions<RoleAccessState, Error>>;
    };
    mutations: {
        toggleRole: () => UseMutationOptions<RoleAccessState, Error, ToggleRolePayload, void>;
    };
};

export const roleAccess: RoleAccessOptions = {
    queries: {
        getRoleAccess: (subscribe: boolean = true): ReturnType<typeof queryOptions<RoleAccessState, Error>> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.enabled-roles`;
            return queryOptions<RoleAccessState, Error>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: `v1.${instanceTopic}.enabled-roles`,
                },
                queryKey: ['role-access', topic],
                queryFn: async (): Promise<RoleAccessState> => {
                    await new Promise((resolve) => setTimeout(resolve, 500));
                    if (simulateQueryError) {
                        throw new Error('Failed to fetch role access settings');
                    }
                    const res = await ResgateManager.model<RoleAccessState>(topic);
                    if (res instanceof ResErr) throw new Error(res.message);

                    return res;
                },
            });
        },
    },
    mutations: {
        toggleRole: (): UseMutationOptions<RoleAccessState, Error, ToggleRolePayload, void> => {
            const queryClient = useQueryClient();
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.enabled-roles`;
            const resgate = ResgateManager.getInstance();

            return {
                mutationFn: async (payload: ToggleRolePayload): Promise<EnabledRoles> => {
                    const roleKey = Role[payload.role];
                    const params: Partial<EnabledRoles> = {
                        [roleKey]: payload.enabled,
                    };
                    const res = await resgate.call<EnabledRoles>(topic, 'set', params);
                    if (simulateMutationError) {
                        throw new Error('Failed to update role access settings');
                    }
                    if (res instanceof ResErr) {
                        throw new TypeError(res.message);
                    }

                    return res;
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['role-access', topic] });
                },
            };
        },
    },
};
